import { Modal, Form, Input, message, Typography, Flex } from 'antd';
import { FC, useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CustomButton } from 'src/components/ui';
import { requestEmailChange, confirmEmailChange } from '@/api/user';
import queryClient from '@/client/queryClient';

interface Props {
  open: boolean;
  currentEmail?: string;
  onClose: () => void;
  /** Called with the new email once the change is confirmed, before refetch. */
  onSuccess?: (newEmail: string) => void;
}

/**
 * Verified email-change flow (TCX-412). Email can no longer be changed through
 * the general profile-update endpoint, so this drives the dedicated two-step
 * flow: request a 4-digit code to the new address, then confirm it.
 */
export const ChangeEmailModal: FC<Props> = ({
  open,
  currentEmail,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailForm] = Form.useForm();
  const [codeForm] = Form.useForm();

  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
    // Reset to a clean state whenever the modal closes, so a request that was
    // cancelled mid-flight can't leave the next open on the verification step.
    if (!open) {
      setStep('email');
      setPendingEmail('');
      emailForm.resetFields();
      codeForm.resetFields();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const requestMutation = useMutation({
    mutationFn: requestEmailChange,
    onSuccess: (_, variables) => {
      if (!openRef.current) return;
      setPendingEmail(variables.email);
      setStep('code');
      message.success('Verification code sent to the new email address.');
    },
    onError: (error: Error) => message.error(error.message),
  });

  const confirmMutation = useMutation({
    mutationFn: confirmEmailChange,
    onSuccess: (data) => {
      message.success('Email updated successfully.');
      // The backend remaps billing contacts to the new email; if that step
      // failed it can't be retried here (email is already changed), so warn
      // the user to re-check their billing settings rather than fail silently.
      if (data?.billingContactsSynced === false) {
        message.warning(
          'Your email changed, but we could not update your billing contacts automatically. Please review them in your contact settings.',
        );
      }
      onSuccess?.(pendingEmail);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      handleClose();
    },
    onError: (error: Error) => message.error(error.message),
  });

  return (
    <Modal
      title={'Change Email'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      {step === 'email' ? (
        <Form
          form={emailForm}
          layout={'vertical'}
          onFinish={(values) =>
            requestMutation.mutate({ email: values.email.trim() })
          }
        >
          <Typography.Paragraph type={'secondary'}>
            Enter your new email address. We&apos;ll send a 4-digit
            verification code there to confirm the change.
          </Typography.Paragraph>
          <Form.Item
            label={'New Email'}
            name={'email'}
            rules={[
              {
                required: true,
                type: 'email',
                message: 'Please enter a valid email address.',
              },
              () => ({
                validator: (_, value) =>
                  value &&
                  value.trim().toLowerCase() ===
                    (currentEmail || '').toLowerCase()
                    ? Promise.reject(
                        new Error('This is already your current email.'),
                      )
                    : Promise.resolve(),
              }),
            ]}
          >
            <Input size={'large'} placeholder={'new@email.com'} />
          </Form.Item>
          <CustomButton
            htmlType={'submit'}
            type={'primary'}
            text={'SEND CODE'}
            block
            loading={requestMutation.isPending}
          />
        </Form>
      ) : (
        <Form
          form={codeForm}
          layout={'vertical'}
          onFinish={(values) =>
            confirmMutation.mutate({ code: values.code.trim() })
          }
        >
          <Typography.Paragraph type={'secondary'}>
            Enter the 4-digit code sent to <strong>{pendingEmail}</strong>. The
            code expires in 5 minutes.
          </Typography.Paragraph>
          <Form.Item
            label={'Verification Code'}
            name={'code'}
            rules={[
              { required: true, message: 'Please enter the verification code.' },
            ]}
          >
            <Input size={'large'} maxLength={4} placeholder={'0000'} />
          </Form.Item>
          <Flex vertical gap={8}>
            <CustomButton
              htmlType={'submit'}
              type={'primary'}
              text={'VERIFY & UPDATE'}
              block
              loading={confirmMutation.isPending}
            />
            <CustomButton
              type={'default'}
              text={'Back'}
              block
              onClick={() => setStep('email')}
            />
          </Flex>
        </Form>
      )}
    </Modal>
  );
};
